import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { qk } from "../keys";
import type { Alert, AlertList, AlertLevel, AlertState } from "../models";

export function useAlerts(params?: {
  site?: number;
  level?: AlertLevel;
  state?: AlertState;
  node?: number;
}) {
  return useQuery({
    queryKey: qk.safety.alerts.list(params),
    queryFn: () =>
      api.get<AlertList>("/alerts/", { params }).then((r) => r.data),
    refetchInterval: 30_000,                       // refresh feed every 30s
  });
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      api.post<Alert>(`/alerts/${id}/acknowledge/`, { note }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
  });
}

export function useClearAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<Alert>(`/alerts/${id}/clear/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
  });
}

/** Aggregate outcome of a bulk ack/clear over N alerts. */
export interface BulkAlertResult {
  ok: number;
  failed: number;
  total: number;
}

/**
 * Run `one(id)` for every id with bounded concurrency, **never rejecting**:
 * each request's success/failure is tallied so a partial failure — e.g. the
 * backend's `400 "already cleared"` on a raced acknowledge, or a network blip —
 * is reported rather than swallowed or aborting the rest of the batch.
 *
 * This replaces the old "fire N `mutate()` in a `forEach`" pattern, which was
 * fire-and-forget (errors vanished, no completion signal) and invalidated the
 * feed once *per* request (an N-fold refetch storm mid-operation).
 */
async function runBulk(
  ids: number[],
  one: (id: number) => Promise<unknown>,
  concurrency = 6,
): Promise<BulkAlertResult> {
  let ok = 0;
  let failed = 0;
  let cursor = 0;
  const worker = async () => {
    while (cursor < ids.length) {
      const id = ids[cursor++];
      try {
        await one(id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
  };
  const pool = Math.max(1, Math.min(concurrency, ids.length));
  await Promise.all(Array.from({ length: pool }, worker));
  return { ok, failed, total: ids.length };
}

/**
 * Bulk-acknowledge many alerts, settling every request and invalidating the
 * feed **once** when the whole batch is done. Returns aggregate counts so the
 * caller can toast partial failures. `isPending` drives the button's loading
 * state (and blocks a double-click from launching a second batch).
 */
export function useBulkAcknowledgeAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      runBulk(ids, (id) =>
        api.post<Alert>(`/alerts/${id}/acknowledge/`, {}).then((r) => r.data),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
  });
}

/** Bulk-clear many alerts — see {@link useBulkAcknowledgeAlerts}. */
export function useBulkClearAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      runBulk(ids, (id) =>
        api.post<Alert>(`/alerts/${id}/clear/`).then((r) => r.data),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
  });
}
